import { LinkBrokenIcon } from "@moznsystems/mozn-ui";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronRightIcon } from "@/components/icons/chevron-right";
import { ClockIcon } from "@/components/icons/clock";
import { ModuleIcon } from "@/components/icons/module";
import { WarningCircleIcon } from "@/components/icons/warning-circle";

interface ErrorFallbackProps {
	error: Error;
	showSidebar: boolean;
}

const getModuleName = (fallback: string): string => {
	const path = window.location.pathname.split("/").filter(Boolean);
	return path[0] || fallback;
};

const formatTimestamps = (): { local: string; utc: string } => {
	const now = new Date();
	return {
		local: now.toLocaleString(),
		utc: now
			.toISOString()
			.replace("T", " ")
			.replace(/\.\d+Z$/, " UTC"),
	};
};

const handleReload = (): void => {
	const url = new URL(window.location.href);
	url.searchParams.set("reload", Date.now().toString());
	window.location.href = url.toString();
};

const MetadataRow = ({
	icon,
	label,
	children,
}: {
	icon: ReactNode;
	label: string;
	children: ReactNode;
}) => (
	<div className="flex gap-2 text-sm items-start">
		<span className="shrink-0">{icon}</span>
		<span style={{ color: "#737180" }} className="font-medium">
			{label}
		</span>
		{children}
	</div>
);

const TerminalBlock = ({ message }: { message: string }) => (
	<div
		className="mt-2 rounded-lg overflow-hidden"
		style={{ backgroundColor: "#222230" }}
	>
		<div
			className="flex items-center gap-1.5 px-3 py-2"
			data-testid="terminal-bar"
		>
			<span className="w-2.5 h-2.5 rounded-full bg-[#F23C3C]" />
			<span className="w-2.5 h-2.5 rounded-full bg-[#F5A623]" />
			<span className="w-2.5 h-2.5 rounded-full bg-[#27AE60]" />
		</div>
		<pre className="px-3 pb-3 text-xs overflow-auto max-h-28 whitespace-pre-wrap break-words font-mono text-[#F87171]">
			{message}
		</pre>
	</div>
);

const ErrorDetails = ({ message }: { message: string }) => {
	const [expanded, setExpanded] = useState(false);
	const { t } = useTranslation("layout");

	return (
		<div className="mx-6 mt-2">
			<button
				type="button"
				onClick={() => setExpanded((prev) => !prev)}
				className="text-sm cursor-pointer select-none flex items-center gap-1.5 py-1.5 rounded-md hover:opacity-70 transition-opacity border-0 outline-none bg-transparent"
				style={{ color: "#737180" }}
			>
				<ChevronRightIcon
					className={`transition-transform duration-200 ${
						expanded ? "rotate-90" : "rtl:-scale-x-100"
					}`}
				/>
				{t("error_fallback.details_toggle")}
			</button>

			<div
				className="overflow-hidden transition-all duration-200"
				style={{
					maxHeight: expanded ? "200px" : "0",
					opacity: expanded ? 1 : 0,
				}}
			>
				<TerminalBlock message={message} />
			</div>
		</div>
	);
};

const ErrorCard = ({
	error,
	isAppLevel,
}: {
	error: Error;
	isAppLevel: boolean;
}) => {
	const { t } = useTranslation("layout");
	const { local, utc } = formatTimestamps();
	const moduleName = isAppLevel
		? t("error_fallback.module_app")
		: getModuleName(t("error_fallback.module_default"));

	return (
		<div
			className="w-full max-w-lg rounded-xl bg-white border border-[var(--c-primary-line)] overflow-hidden"
			style={{ boxShadow: "0 5px 50px rgba(0,23,46,0.08)" }}
		>
			{/* Header */}
			<div className="px-6 pt-6 pb-4 flex items-start gap-3">
				<WarningCircleIcon className="shrink-0" />
				<div className="pt-1">
					<h2
						className="text-lg font-bold leading-tight mt-1 mb-1"
						style={{ color: "#222230", fontFamily: "Montserrat, sans-serif" }}
					>
						{t("error_fallback.title")}
					</h2>
					<p
						className="text-sm mt-0 leading-relaxed"
						style={{ color: "#737180", fontFamily: "Montserrat, sans-serif" }}
					>
						{isAppLevel
							? t("error_fallback.description_app")
							: t("error_fallback.description_module")}
					</p>
				</div>
			</div>

			{/* Metadata */}
			<div className="mx-6 py-3 space-y-2.5 border-t border-[var(--c-primary-line)]">
				<MetadataRow icon={<ModuleIcon />} label={t("error_fallback.module")}>
					<span
						className="ms-auto font-semibold capitalize"
						style={{ color: "#222230" }}
					>
						{moduleName}
					</span>
				</MetadataRow>

				<MetadataRow
					icon={<LinkBrokenIcon width="16" height="16" color="#737180" />}
					label={t("error_fallback.url")}
				>
					<span
						className="ms-auto font-mono text-xs truncate max-w-[280px]"
						style={{ color: "#222230" }}
					>
						{window.location.pathname}
					</span>
				</MetadataRow>

				<MetadataRow icon={<ClockIcon />} label={t("error_fallback.time")}>
					<div className="flex flex-col items-end ms-auto">
						<span style={{ color: "#222230" }}>{local}</span>
						<span className="text-xs" style={{ color: "#737180" }}>
							({utc})
						</span>
					</div>
				</MetadataRow>
			</div>

			<ErrorDetails message={error.message} />

			{/* Reload button */}
			<div className="px-6 pt-4 pb-6">
				<button
					type="button"
					onClick={handleReload}
					className="w-full py-2.5 text-white font-bold text-sm flex items-center justify-center gap-2 transition-opacity hover:opacity-90 border-0 outline-none"
					style={{
						backgroundColor: "#7370FF",
						borderRadius: "7px",
						fontFamily: "Montserrat, sans-serif",
					}}
				>
					{t("error_fallback.reload")}
				</button>
			</div>
		</div>
	);
};

export const ErrorFallback = ({ error, showSidebar }: ErrorFallbackProps) => {
	const card = (
		<div className="flex items-center justify-center min-h-[60vh] p-6">
			<ErrorCard error={error} isAppLevel={!showSidebar} />
		</div>
	);

	if (showSidebar) {
		return card;
	}

	return (
		<div
			className="fixed inset-0 flex items-center justify-center"
			style={{ backgroundColor: "#F7F7F7" }}
		>
			{card}
		</div>
	);
};
